import type { HeadingLevel, HeadingProps } from "@components/core/Heading";
import type { ILink } from "@components/core/Link";
import type { VariantProps } from "class-variance-authority";

import { Link } from "@components/core/Link";
import { isValidLinkWithLabel } from "@ui/lib/link";
import { cn } from "@ui/lib/utils";
import { cva } from "class-variance-authority";
import { ICmsComplexTextField } from "@mw/shared/payload-types";

const headingSectionVariants = cva("text-balance", {
  variants: {
    variant: {
      expressive: "f-heading-01 grid-col-span-12",
      xxlarge: "f-heading-01 grid-col-span-12",
      xlarge: "f-heading-02 grid-col-span-12",
      large: "f-heading-03 grid-col-span-12",
      small: "f-heading-04 grid-col-span-12",
    },
    compact: {
      true: "",
      false: "",
    },
    balance: {
      true: "text-balance",
      false: "",
    },
    centered: {
      true: "lg:grid-col-start-2 lg:grid-col-span-10 text-center",
      false: "",
    },
  },
  compoundVariants: [
    {
      variant: ["xxlarge", "xlarge", "large", "small"],
      compact: true,
      centered: false,
      class: "lg:grid-col-span-8 xl:grid-col-span-7 xxl:pr-40",
    },
    {
      variant: ["xxlarge", "xlarge", "large", "small"],
      compact: false,
      centered: false,
      class: "lg:grid-col-span-9",
    },
  ],
  defaultVariants: {
    variant: "large",
    compact: false,
    balance: true,
  },
});

export interface HeadingSectionProps
  extends Omit<HeadingProps, "level" | "title">, VariantProps<typeof headingSectionVariants> {
  level?: HeadingLevel;
  cta?: ILink;
  description?: null | string;
  descriptionTag?: NonNullable<ICmsComplexTextField["options"]>["tag"];
  titleTag?: NonNullable<ICmsComplexTextField["options"]>["tag"];
  headingClassName?: string;
  hideCtaOnMobile?: boolean;
  title?: string | null;
}

const HeadingSection = ({
  children,
  hideCtaOnMobile = false,
  description,
  descriptionTag,
  level,
  variant,
  compact = false,
  className,
  title,
  cta,
  balance,
  headingClassName,
  centered = false,
  titleTag,
  ...props
}: HeadingSectionProps) => {
  const withCta = cta && isValidLinkWithLabel(cta);

  const TitleTag = (titleTag ?? (level ? `h${level}` : "h2")) as any;
  const DescriptionTag = (descriptionTag || "p") as any;

  return (
    <div className={cn("grid-layout grid-col-span-12 gap-y-0", className)}>
      <TitleTag
        className={headingSectionVariants({ compact, variant, balance, className: headingClassName, centered })}
        {...props}
      >
        {title && <span dangerouslySetInnerHTML={{ __html: title }} />}
        {children}
      </TitleTag>
      {withCta && (
        <div
          className={cn(
            variant === "expressive"
              ? "grid-col-span-12 mt-28"
              : compact
                ? "grid-col-span-12 mt-28 flex items-center lg:grid-col-span-4 xl:grid-col-span-5 lg:mt-0 lg:justify-end"
                : "grid-col-span-12 mt-28 flex items-center lg:grid-col-span-3 lg:mt-0 lg:justify-end",
            hideCtaOnMobile ? "hidden lg:flex" : "",
          )}
        >
          <Link asButton buttonVariants={{ size: "md", variant: "secondary" }} {...cta} />
        </div>
      )}
      {description && (
        <DescriptionTag
          className={cn(
            "f-paragraph-02 grid-col-span-12 mt-12 lg:grid-col-span-6",
            centered ? "text-center lg:grid-col-start-4" : "",
          )}
        >
          {description}
        </DescriptionTag>
      )}
    </div>
  );
};

HeadingSection.displayName = "HeadingSection";

export { HeadingSection, headingSectionVariants };
